import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, onValue, set } from 'firebase/database';

export default function TaxCollectionForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize with values from the uploaded image
  const [data, setData] = useState({
    maalmataaKr: "",
    vasoolDinaank: new Date().toISOString().split('T')[0],
    paavatiKr: "",
    milkatDhaarak: "",
    karBharnaaryaacheNaav: "",
    gharpatti: 0,
    vij: 0,
    aarogya: 0,
    noticeFee: 0,
    japtFee: 0,
    penalty: 0,
    discount: 0,
    paymentMode: "रोख"
  });

  // Fetch user data from Firebase
  useEffect(() => {
    const userPropertyNo = location.state?.userData?.id;
    
    if (userPropertyNo) {
      const userRecordRef = ref(database, `Grampanchayat/Gramniddhi/NewMilakhatRecords/${userPropertyNo}`);
      
      const unsubscribe = onValue(userRecordRef, (snapshot) => {
        const record = snapshot.val();
        if (record) {
          setUserData(record);
          setData(prev => ({
            ...prev,
            maalmataaKr: userPropertyNo,
            milkatDhaarak: record.mainOwner || '',
            karBharnaaryaacheNaav: record.mainOwner || '',
            gharpatti: record.taxData?.houseTax?.total || 0,
            vij: record.taxData?.electricityTax?.total || 0,
            aarogya: record.taxData?.healthTax?.total || 0,
            pending: record.taxData?.total?.total || 0
          }));
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching user data:", error);
        setLoading(false);
      });

      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [location.state]);

  // Tax data from fetched user data
  const taxData = [
    { name: "घरपट्टी", prev: userData?.taxData?.houseTax?.previous || 0, curr: userData?.taxData?.houseTax?.current || 0, dey: userData?.taxData?.houseTax?.total || 0, key: "gharpatti" },
    { name: "विज कर", prev: userData?.taxData?.electricityTax?.previous || 0, curr: userData?.taxData?.electricityTax?.current || 0, dey: userData?.taxData?.electricityTax?.total || 0, key: "vij" },
    { name: "आरोग्य कर", prev: userData?.taxData?.healthTax?.previous || 0, curr: userData?.taxData?.healthTax?.current || 0, dey: userData?.taxData?.healthTax?.total || 0, key: "aarogya" },
  ];

  // Calculate total tax paid
  const totalTax =
    Number(data.gharpatti) +
    Number(data.vij) +
    Number(data.aarogya);

  // Check if all tax amounts are filled (greater than 0)
  const allTaxAmountsFilled = 
    Number(data.gharpatti) > 0 && 
    Number(data.vij) > 0 && 
    Number(data.aarogya) > 0;

  // Calculate 5% discount if all amounts are filled
  const calculatedDiscount = allTaxAmountsFilled ? totalTax * 0.05 : 0;

  // Update discount field automatically
  React.useEffect(() => {
    if (allTaxAmountsFilled && calculatedDiscount !== Number(data.discount)) {
      setData(prev => ({ ...prev, discount: calculatedDiscount.toFixed(2) }));
    } else if (!allTaxAmountsFilled && Number(data.discount) > 0) {
      setData(prev => ({ ...prev, discount: 0 }));
    }
  }, [totalTax, allTaxAmountsFilled]);

  // Calculate grand total: totalTax + noticeFee + japtFee + penalty - discount
  const grandTotal =
    totalTax +
    Number(data.noticeFee) +
    Number(data.japtFee) +
    Number(data.penalty) -
    Number(data.discount);

  const input =
    "w-full border border-primary_dark/40 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary";

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value });
  };

  const handleTaxAmountChange = (field, value) => {
    const numValue = Number(value) || 0;
    setData({ ...data, [field]: numValue });
  };

  const handleSave = () => {
    // Validate required fields
    if (!data.maalmataaKr) {
      alert('कृपया मालमत्ता क्र. प्रविष्ट करा');
      return;
    }
    
    if (!data.paavatiKr) {
      alert('कृपया पावती क्र. प्रविष्ट करा');
      return;
    }

    // Show confirmation popup
    const isConfirmed = window.confirm('तुम्ही ही कर भरणी माहिती जतन करू इच्छिता? पावती PDF तयार होईल.');
    if (!isConfirmed) {
      return;
    }

    // Prepare tax collection data
    const taxCollectionData = {
      maalmataaKr: data.maalmataaKr,
      vasoolDinaank: data.vasoolDinaank,
      paavatiKr: data.paavatiKr,
      milkatDhaarak: data.milkatDhaarak,
      karBharnaaryaacheNaav: data.karBharnaaryaacheNaav,
      taxDetails: {
        gharpatti: {
          previous: userData?.taxData?.houseTax?.previous || 0,
          current: userData?.taxData?.houseTax?.current || 0,
          total: userData?.taxData?.houseTax?.total || 0,
          paid: data.gharpatti
        },
        vij: {
          previous: userData?.taxData?.electricityTax?.previous || 0,
          current: userData?.taxData?.electricityTax?.current || 0,
          total: userData?.taxData?.electricityTax?.total || 0,
          paid: data.vij
        },
        aarogya: {
          previous: userData?.taxData?.healthTax?.previous || 0,
          current: userData?.taxData?.healthTax?.current || 0,
          total: userData?.taxData?.healthTax?.total || 0,
          paid: data.aarogya
        }
      },
      otherCharges: {
        noticeFee: Number(data.noticeFee),
        japtFee: Number(data.japtFee),
        penalty: Number(data.penalty),
        discount: Number(data.discount)
      },
      totals: {
        totalTax: totalTax,
        grandTotal: grandTotal
      },
      paymentMode: data.paymentMode,
      timestamp: new Date().toISOString(),
      originalUserData: userData // Store reference to original user data
    };

    // Save to Firebase under /Grampanchayat/Gramniddhi/karbharna/{maalmataaKr}/{paavatiKr}
    const collectionPath = `Grampanchayat/Gramniddhi/karbharna/${data.maalmataaKr}/${data.paavatiKr}`;
    const collectionRef = ref(database, collectionPath);
    
    set(collectionRef, taxCollectionData)
      .then(() => {
        alert('कर भरणी माहिती यशस्वीरित्या जतन केली गेली!');
        // Generate PDF after successful save
        generatePDF(taxCollectionData);
        // Navigate back to tax receipt page after successful save
        navigate('/tax-receipt');
      })
      .catch((error) => {
        console.error("Error saving tax collection data: ", error);
        alert('माहिती जतन करण्यात त्रुटी आली. कृपया पुन्हा प्रयत्न करा.');
      });
  };

  const generatePDF = (taxData) => {
    // Create a new window for the printable receipt
    const printWindow = window.open('', '_blank');
    
    // Generate the HTML content for the receipt
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tax Receipt - पावती क्रमांक ${taxData.paavatiKr}</title>
        <style>
          @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
            .receipt-container { 
              display: flex; 
              justify-content: space-between; 
              width: 100%;
              page-break-after: always;
            }
            .receipt { 
              width: 48%; 
              border: 1px solid #000;
              padding: 10px;
              font-family: Arial, sans-serif;
            }
            .header { text-align: center; font-weight: bold; margin-bottom: 10px; }
            .title { font-size: 14px; margin: 5px 0; }
            .subtitle { font-size: 12px; margin: 5px 0; }
            .info { margin: 8px 0; font-size: 10px; }
            .table { 
              border-collapse: collapse; 
              width: 100%; 
              font-size: 9px;
              margin: 10px 0;
            }
            .table td, .table th { 
              border: 1px solid #000; 
              padding: 2px 4px; 
              text-align: left;
            }
            .table th { font-weight: bold; }
            .table .text-center { text-align: center; }
            .table .text-right { text-align: right; }
            .footer { margin-top: 15px; font-size: 9px; }
            .signature { margin-top: 20px; font-size: 9px; }
          }
          @media screen {
            body { margin: 20px; }
            .receipt-container { 
              display: flex; 
              justify-content: space-between; 
              width: 100%;
            }
            .receipt { 
              width: 48%; 
              border: 1px solid #000;
              padding: 10px;
              font-family: Arial, sans-serif;
            }
            .header { text-align: center; font-weight: bold; margin-bottom: 10px; }
            .title { font-size: 14px; margin: 5px 0; }
            .subtitle { font-size: 12px; margin: 5px 0; }
            .info { margin: 8px 0; font-size: 10px; }
            .table { 
              border-collapse: collapse; 
              width: 100%; 
              font-size: 9px;
              margin: 10px 0;
            }
            .table td, .table th { 
              border: 1px solid #000; 
              padding: 2px 4px; 
              text-align: left;
            }
            .table th { font-weight: bold; }
            .table .text-center { text-align: center; }
            .table .text-right { text-align: right; }
            .footer { margin-top: 15px; font-size: 9px; }
            .signature { margin-top: 20px; font-size: 9px; }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="text-align: center; margin-bottom: 20px;">
          <h2>कर भरणी पावती</h2>
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">प्रिंट करा / PDF डाउनलोड करा</button>
          <p style="margin-top: 10px; color: #666;">Print करण्यासाठी वरील बटण दाबा किंवा Ctrl+P वापरा</p>
        </div>
        
        <div class="receipt-container">
          <!-- First Receipt -->
          <div class="receipt">
            <div class="header">
              <div class="title">पावती क्रमांक : ${taxData.paavatiKr}</div>
              <div class="title">ग्रामपंचायत इनाम धामणी</div>
              <div class="subtitle">नमुना नं १० कराबद्दल पावती</div>
            </div>
            
            <div class="info">
              <div><strong>मालक : / घर नंबर :</strong> ${taxData.milkatDhaarak} ${taxData.maalmataaKr}</div>
              <div><strong>कर भरणारा :</strong> ${taxData.karBharnaaryaacheNaav}</div>
              <div>याबद्दल सन 2025 - 2026 करीता पुढे नमुद केलेल्या रकमा करादाखल मिळाल्या.</div>
            </div>
            
            <table class="table">
              <thead>
                <tr>
                  <th>करांची नावे</th>
                  <th class="text-center">मागील बाकी</th>
                  <th class="text-center">चालू कर</th>
                  <th class="text-center">एकुण</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>घरपट्टी</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.gharpatti.paid}</td>
                  <td class="text-center">${taxData.taxDetails.gharpatti.paid}</td>
                </tr>
                <tr>
                  <td>विज कर</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.vij.paid}</td>
                  <td class="text-center">${taxData.taxDetails.vij.paid}</td>
                </tr>
                <tr>
                  <td>आरोग्य कर</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.aarogya.paid}</td>
                  <td class="text-center">${taxData.taxDetails.aarogya.paid}</td>
                </tr>
                <tr>
                  <td>नोटीस फी</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr>
                  <td>जप्ती फी</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr>
                  <td>घरपट्टी थकबाकी दंड</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr style="font-weight: bold;">
                  <td>एकूण जमा</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.totals.totalTax}</td>
                  <td class="text-center">${taxData.totals.totalTax}</td>
                </tr>
                <tr>
                  <td>करातुन सुट वजा</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.otherCharges.discount}</td>
                </tr>
                <tr style="font-weight: bold;">
                  <td></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.totals.grandTotal}</td>
                </tr>
              </tbody>
            </table>
            
            <div class="footer">
              <div><strong>तारीख :</strong> ${taxData.vasoolDinaank}</div>
              <div class="signature">वसुल करणा-या कारकुनाच्या सहीची अद्याक्षरे</div>
              <div style="text-align: center; margin-top: 10px; font-style: italic;">Printed From Gram Soft®</div>
            </div>
          </div>
          
          <!-- Second Receipt (Duplicate) -->
          <div class="receipt">
            <div class="header">
              <div class="title">पावती क्रमांक : ${taxData.paavatiKr}</div>
              <div class="title">ग्रामपंचायत इनाम धामणी</div>
              <div class="subtitle">नमुना नं १० कराबद्दल पावती</div>
            </div>
            
            <div class="info">
              <div><strong>मालक : / घर नंबर :</strong> ${taxData.milkatDhaarak} ${taxData.maalmataaKr}</div>
              <div><strong>कर भरणारा :</strong> ${taxData.karBharnaaryaacheNaav}</div>
              <div>याबद्दल सन 2025 - 2026 करीता पुढे नमुद केलेल्या रकमा करादाखल मिळाल्या.</div>
            </div>
            
            <table class="table">
              <thead>
                <tr>
                  <th>करांची नावे</th>
                  <th class="text-center">मागील बाकी</th>
                  <th class="text-center">चालू कर</th>
                  <th class="text-center">एकुण</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>घरपट्टी</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.gharpatti.paid}</td>
                  <td class="text-center">${taxData.taxDetails.gharpatti.paid}</td>
                </tr>
                <tr>
                  <td>विज कर</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.vij.paid}</td>
                  <td class="text-center">${taxData.taxDetails.vij.paid}</td>
                </tr>
                <tr>
                  <td>आरोग्य कर</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.taxDetails.aarogya.paid}</td>
                  <td class="text-center">${taxData.taxDetails.aarogya.paid}</td>
                </tr>
                <tr>
                  <td>नोटीस फी</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr>
                  <td>जप्ती फी</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr>
                  <td>घरपट्टी थकबाकी दंड</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
                <tr style="font-weight: bold;">
                  <td>एकूण जमा</td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.totals.totalTax}</td>
                  <td class="text-center">${taxData.totals.totalTax}</td>
                </tr>
                <tr>
                  <td>करातुन सुट वजा</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.otherCharges.discount}</td>
                </tr>
                <tr style="font-weight: bold;">
                  <td></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center">${taxData.totals.grandTotal}</td>
                </tr>
              </tbody>
            </table>
            
            <div class="footer">
              <div><strong>तारीख :</strong> ${taxData.vasoolDinaank}</div>
              <div class="signature">वसुल करणा-या कारकुनाच्या सहीची अद्याक्षरे</div>
              <div style="text-align: center; margin-top: 10px; font-style: italic;">Printed From Gram Soft®</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Write the HTML content to the new window
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex items-center justify-center">
        <div className="text-lg text-gray-700">Loading user data...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex items-center justify-center">
        <div className="text-lg text-red-600">No user data found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto bg-white border border-primary rounded shadow-lg">

        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-2 font-semibold text-lg">
          कर वसूल पावती
        </div>

        {/* Basic Info */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">मालमत्ता क्र.</label>
            <input 
              className={`${input} bg-gray-100`} 
              value={data.maalmataaKr} 
              disabled 
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">वसूल दिनांक</label>
            <input 
              type="date" 
              className={input} 
              value={data.vasoolDinaank}
              onChange={(e) => handleChange("vasoolDinaank", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">पावती क्र.</label>
            <input 
              className={input} 
              placeholder="पावती क्र. भरा" 
              value={data.paavatiKr}
              onChange={(e) => handleChange("paavatiKr", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">मिळकत धारक</label>
            <input 
              className={`${input} bg-gray-100`} 
              value={data.milkatDhaarak} 
              disabled 
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">कर भरणाऱ्याचे नाव</label>
            <div className="flex">
              <input
                className={`${input} rounded-r-none bg-gray-100`}
                value={data.karBharnaaryaacheNaav}
                disabled
              />
              <button className="bg-primary text-white px-4 rounded-r hover:bg-primary-dark">
                ...
              </button>
            </div>
          </div>
        </div>

        {/* Tax Table */}
        <div className="px-4 pb-2">
          <div className="bg-primary text-white px-2 py-1 text-sm font-semibold">
            कर जमा
          </div>
          <table className="w-full border border-gray-300 text-sm">
            <thead className="bg-primary-dark text-white">
              <tr>
                <th className="border border-gray-300 p-2">कराचे नाव</th>
                <th className="border border-gray-300 p-2">मागील वर्ष</th>
                <th className="border border-gray-300 p-2">चालू वर्ष</th>
                <th className="border border-gray-300 p-2">देय रक्कम</th>
                <th className="border border-gray-300 p-2">भरलेली रक्कम</th>
              </tr>
            </thead>
            <tbody>
              {taxData.map((t) => (
                <tr key={t.key}>
                  <td className="border border-gray-300 p-2">{t.name}</td>
                  <td className="border border-gray-300 p-2 text-center">{t.prev}</td>
                  <td className="border border-gray-300 p-2 text-center">{t.curr}</td>
                  <td className="border border-gray-300 p-2 text-center">{t.dey}</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      className={input}
                      value={data[t.key]}
                      onChange={(e) => handleTaxAmountChange(t.key, e.target.value)}
                    />
                  </td>
                </tr>
              ))}

              <tr className="bg-blue-100 font-semibold">
                <td className="border border-gray-300 p-2">एकूण</td>
                <td className="border border-gray-300 p-2 text-center">
                  {taxData.reduce((sum, t) => sum + t.prev, 0)}
                </td>
                <td className="border border-gray-300 p-2 text-center">
                  {taxData.reduce((sum, t) => sum + t.curr, 0)}
                </td>
                <td className="border border-gray-300 p-2 text-center">
                  {taxData.reduce((sum, t) => sum + t.dey, 0)}
                </td>
                <td className="border border-gray-300 p-2 text-center bg-white">{totalTax}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Other Charges Section */}
        <div className="px-4 py-2">
          <div className="bg-primary text-white px-2 py-1 text-sm font-semibold">
            इतर जमा
          </div>
        </div>

        {/* Other Charges */}
        <div className="px-4 pb-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm">ग्रामनिधी नोटीस फी</span>
            <input
              type="number"
              className={`${input} w-48`}
              value={data.noticeFee}
              onChange={(e) => handleChange("noticeFee", e.target.value)}
            />
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm">ग्रामनिधी जप्ती फी</span>
            <input
              type="number"
              className={`${input} w-48`}
              value={data.japtFee}
              onChange={(e) => handleChange("japtFee", e.target.value)}
            />
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm">मागील बाकी दंड</span>
            <input
              type="number"
              className={`${input} w-48`}
              value={data.penalty}
              onChange={(e) => handleChange("penalty", e.target.value)}
            />
          </div>

          <div className="flex justify-between items-center border-t pt-3">
            <span className="text-sm font-medium">
              करामधील सूट {allTaxAmountsFilled && <span className="text-green-600 text-xs">(5% ऑटोमॅटिक सूट)</span>}
            </span>
            <input
              type="number"
              className={`${input} w-48 bg-gray-100`}
              value={data.discount}
              disabled={allTaxAmountsFilled}
              onChange={(e) => handleChange("discount", e.target.value)}
            />
          </div>

          {allTaxAmountsFilled && (
            <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-800">
              ✓ सर्व कर रक्कम भरल्यामुळे 5% सूट लागू केली गेली: ₹{calculatedDiscount.toFixed(2)}
            </div>
          )}

          <p className="text-red-600 text-xs text-right">
            घरपट्टीमधील सूट फक्त चालू घरपट्टीवर लागू आहे. तसेच घर पूर्ण रक्कम एकत्र केली भरली तर मागणी कर गणना केली जाईल. सर्व कर रक्कम भरल्यास 5% अपंगत सूट मिळेल.
          </p>
        </div>

        {/* Total */}
        <div className="px-4 pb-4">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-lg">एकूण देय रक्कम</span>
            <span className="bg-primary text-white px-8 py-2 rounded font-bold text-lg">
              {grandTotal}
            </span>
          </div>
        </div>

        {/* Payment Mode */}
        <div className="bg-primary/20 px-4 py-3 flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="radio" 
              name="pay" 
              value="रोख"
              checked={data.paymentMode === "रोख"}
              onChange={(e) => handleChange("paymentMode", e.target.value)}
            /> 
            <span className="font-medium">रोख</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="radio" 
              name="pay" 
              value="धनादेश"
              checked={data.paymentMode === "धनादेश"}
              onChange={(e) => handleChange("paymentMode", e.target.value)}
            /> 
            <span className="font-medium">धनादेश</span>
          </label>
        </div>

        {/* Buttons */}
        <div className="bg-primary p-4 flex justify-center gap-4">
          <button 
            onClick={handleSave}
            className="bg-green-600 text-white px-8 py-2 rounded font-semibold hover:bg-green-700 transition"
          >
            साठवा
          </button>
          <button 
            onClick={() => navigate('/tax-receipt')}
            className="bg-red-600 text-white px-8 py-2 rounded font-semibold hover:bg-red-700 transition"
          >
            रद्द करा
          </button>
        </div>

      </div>
    </div>
  );
}
